import React, { useContext, useState, useEffect } from 'react';
import { ShoppingCart, Zap, TrendingUp, Wallet } from 'lucide-react';
import axios from 'axios';
import { ApiLink } from '../../APILink/ApiLink';
import authHeader from '../../Auth/authHeader';
import { FiscalYearContext } from '../../FiscalYear/FiscalYearContext';
import { errorAlert } from '../../Components/Alerts';
import KPICard, { KPICardSkeleton } from './DashboardComponents/KPICard';
import NewChart from './DashboardComponents/NewChart';
import BankCashPanel from './DashboardComponents/BankCashPanel';
import RecentTransactions from './DashboardComponents/RecentTransactions';
import ExpensePieChart from './DashboardComponents/ExpensePieChart';
import './DashboardPage.scss';

export default function DashboardPage() {
  const [salesTotal, setSalesTotal] = useState(0);
  const [purchaseTotal, setPurchaseTotal] = useState(0);
  const [investmentTotal, setInvestmentTotal] = useState(0);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [salesTrend, setSalesTrend] = useState(0);
  const [purchaseTrend, setPurchaseTrend] = useState(0);
  const [investmentTrend, setInvestmentTrend] = useState(0);
  const [expenseTrend, setExpenseTrend] = useState(0);
  const [loading, setLoading] = useState(true);

  const { fiscalYear } = useContext(FiscalYearContext);
  const headers = authHeader();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [totalsResponse, trendsResponse] = await Promise.all([
        axios.get(
          ApiLink.API_URL + 'Dashboard/totalValues',
          {
            headers,
            params: { fiscalYear: fiscalYear }
          }
        ),
        axios.get(
          ApiLink.API_URL + 'Dashboard/totalValuesYtd',
          {
            headers,
            params: { fiscalYear: fiscalYear }
          }
        )
      ]);

      if (totalsResponse.data && totalsResponse.data.length > 0) {
        const data = totalsResponse.data[0];
        setSalesTotal(data.TotalSales || 0);
        setPurchaseTotal(data.PurchaseOrders || 0);
        setInvestmentTotal(data.TotalInvestments || 0);
        setExpenseTotal(data.TotalExpenses || 0);
      }

      if (trendsResponse.data && trendsResponse.data.length > 0) {
        const trendData = trendsResponse.data[0];
        setSalesTrend(Number(trendData.SalesTrend) || 0);
        setPurchaseTrend(Number(trendData.PurchaseOrdersTrend) || 0);
        setInvestmentTrend(Number(trendData.InvestmentsTrend) || 0);
        setExpenseTrend(Number(trendData.ExpensesTrend) || 0);
      }

      console.log(trendsResponse.data);
    } catch (error) {
      errorAlert('Gabim në kërkesë:', error);
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert('Gabim i panjohur në kërkesë. Shih konsolën për detaje.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fiscalYear]);

  const formatNumber = (num) => {
    return num ? num.toLocaleString('en-US', { minimumFractionDigits: 0 }) : '0.00';
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="kpi-grid">
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
        </div>

        <div className="dashboard-chart-row">
          <NewChart />
          <BankCashPanel />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="kpi-grid">
        <KPICard
          title="Shitjet Totale"
          value={`€ ${formatNumber(salesTotal)}`}
          icon={ShoppingCart}
          color="green"
          trend={salesTrend}
          trendLabel="vitit kaluar"
          to="/Sales/salesintotal"
          linkLabel="Lista Shitjeve"
        />
        <KPICard
          title="Blerjet"
          value={`€ ${formatNumber(purchaseTotal)}`}
          icon={TrendingUp}
          color="orange"
          trend={purchaseTrend}
          trendLabel="vitit kaluar"
          to="/purchases/purchaseintotal"
          linkLabel="Lista Blerjeve"
        />
        <KPICard
          title="Blerjet Investive"
          value={`€ ${formatNumber(investmentTotal)}`}
          icon={Zap}
          color="blue"
          trend={investmentTrend}
          trendLabel="vitit kaluar"
          to="/purchases/investmentpurchases"
          linkLabel="Lista Blerjeve Investive"
        />
        <KPICard
          title="Shpenzimet"
          value={`€ ${formatNumber(expenseTotal)}`}
          icon={Wallet}
          color="red"
          trend={expenseTrend}
          trendLabel="vitit kaluar"
          to="/expences/expensepurchases"
          linkLabel="Lista Shpenzimeve"
        />
      </div>

      <div className="dashboard-chart-row">
        <NewChart />
        <BankCashPanel />
      </div>

      <div className="dashboard-bottom-row">
        <RecentTransactions />
        <ExpensePieChart />
      </div>
    </div>
  );
}